import { useCallback, useEffect, useMemo, useState } from 'react';
import { NovaMark } from '../../design/Nova';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../../design/icons';
import { IconButton } from '../../design/primitives/Button';
import { Breadcrumb, Kbd } from '../../design/primitives/Misc';
import { Tooltip } from '../../design/primitives/Overlay';
import { useTheme } from '../../design/theme';
import { InspectorDock, useInspectorRouteReset } from '../../views/Inspector';
import { CommandPalette } from '../../layout/CommandPalette';
import { NotificationTray } from '../../layout/NotificationTray';
import { FeedbackButton } from '../../views/FeedbackButton';
import { ShortcutSheet } from '../../layout/shortcuts';
import { useBreadcrumbs } from '../../layout/AppShell';
import { UserMenu } from '../UserMenu';
import { useIsPhone } from '../../design/useMediaQuery';
import { useShellChrome } from '../useShellChrome';
import { useRecordTabs } from '../useRecordTabs';
import { useRouteRecord } from '../useRouteRecord';
import { areaForPath } from '../areas';
import type { ShellProps } from '../types';
import { VersionSkewChip } from '../VersionSkewChip';

// Workspace skin (default): a thin left AREA rail (~6 areas) whose selection swaps a
// contextual sidebar to just that area's nav — so you never face 41 items at once. A topbar
// carries global search / notifications / theme, and a record-tabs strip keeps several
// records open at once. Content is the routed <Outlet/>; the Inspector dock is preserved.

const AREA_KEY = 'hf.ws.area';

export function WorkspaceShell({ brand = 'NovaPSA', brandShort = 'Nova', badges, footer, sections, areas }: ShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  // On a phone the rail is the bottom tab bar; an account button there competes with the
  // areas for width and gets clipped, so it moves up to the top bar instead.
  const isPhone = useIsPhone();
  const { paletteOpen, setPaletteOpen, sheetOpen, setSheetOpen } = useShellChrome(sections);
  const crumbs = useBreadcrumbs();
  const tabs = useRecordTabs();
  const record = useRouteRecord();
  const [mobileNav, setMobileNav] = useState(false);
  useInspectorRouteReset(location.pathname);

  const routeArea = useMemo(() => areaForPath(areas, location.pathname), [areas, location.pathname]);
  const [selectedArea, setSelectedArea] = useState<string>(() => {
    try {
      const v = sessionStorage.getItem(AREA_KEY);
      if (v && areas.some((a) => a.id === v)) return v;
    } catch {
      /* ignore */
    }
    return routeArea ?? areas[0]?.id ?? 'home';
  });

  // The route is the source of truth: when you navigate (link, palette, chord) into an area,
  // the rail follows. Manual rail clicks also navigate, so the two stay in lockstep.
  useEffect(() => {
    if (routeArea && routeArea !== selectedArea) setSelectedArea(routeArea);
  }, [routeArea]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try {
      sessionStorage.setItem(AREA_KEY, selectedArea);
    } catch {
      /* ignore */
    }
  }, [selectedArea]);

  useEffect(() => setMobileNav(false), [location.pathname]);

  // Add a record tab when the route first reaches a detail record (path-change only, so a
  // manual close stays closed); update its label separately once the record's name loads.
  useEffect(() => {
    if (record) tabs.open(record.path, record.label);
  }, [record?.path]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (record) tabs.rename(record.path, record.label);
  }, [record?.path, record?.label]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = areas.find((a) => a.id === selectedArea) ?? areas[0];

  const pickArea = useCallback(
    (areaId: string) => {
      setSelectedArea(areaId);
      const area = areas.find((a) => a.id === areaId);
      const first = area?.sections[0]?.items[0];
      // Only jump if we're not already somewhere inside this area.
      if (first && areaForPath(areas, location.pathname) !== areaId) navigate(first.to);
    },
    [areas, location.pathname, navigate],
  );

  const tabActive = (path: string) => path.split('?')[0] === location.pathname;

  return (
    <div className={['hf-ws', mobileNav && 'nav-open'].filter(Boolean).join(' ')}>
      <a href="#hf-main" className="hf-skip">Skip to content</a>
      {mobileNav && <div className="hf-shell-scrim" onClick={() => setMobileNav(false)} aria-hidden />}

      {/* ── Area rail ─────────────────────────────────────────────────────── */}
      <nav className="hf-ws-rail" aria-label="Areas">
        <Link to="/" className="hf-ws-brand" aria-label={typeof brand === 'string' ? `${brand} — home` : 'Home'}>
          <NovaMark size={26} className="mark" />
          <span className="short hf-display">{brandShort}</span>
        </Link>
        <div className="hf-ws-areas">
          {areas.map((a) => (
            <Tooltip key={a.id} content={a.label} placement="right-start">
              <button
                type="button"
                className={['hf-ws-area', a.id === selectedArea && !!routeArea && 'active'].filter(Boolean).join(' ')}
                aria-current={a.id === selectedArea && !!routeArea ? 'page' : undefined}
                onClick={() => pickArea(a.id)}
              >
                <Icon name={a.icon} size={20} />
                <span className="lbl">{a.label}</span>
              </button>
            </Tooltip>
          ))}
        </div>
        {!isPhone && (
          <div className="hf-ws-rail-foot">
            <UserMenu placement="right-start" onShortcuts={() => setSheetOpen(true)} />
          </div>
        )}
      </nav>

      {/* ── Contextual sidebar ────────────────────────────────────────────────
          Hidden on Home (a route with no area): there's nothing to list, so the main
          content takes the full width instead of a one-item sidebar with a duplicate header. */}
      {routeArea && (
      <aside className="hf-ws-side" aria-label={current ? `${current.label} navigation` : 'Navigation'}>
        <div className="hf-ws-side-head">
          <span className="hf-display" title={typeof brand === 'string' ? brand : undefined}>{current?.label ?? brand}</span>
          <IconButton icon="x" label="Close navigation" size="sm" className="hf-ws-side-close" onClick={() => setMobileNav(false)} />
        </div>
        <button type="button" className="hf-search-trigger" onClick={() => setPaletteOpen(true)} aria-label="Search and commands">
          <Icon name="search" size={14} />
          <span className="lbl">Search…</span>
          <span className="kbd"><Kbd>⌘K</Kbd></span>
        </button>
        <div className="hf-ws-nav hf-scroll">
          {(current?.sections ?? []).map((s) => (
            <div key={`${s.area ?? ''}::${s.label}`} className="hf-nav-section">
              {(current?.sections.length ?? 0) > 1 && <div className="hf-nav-label">{s.label}</div>}
              {s.items.map((it) => {
                const badge = typeof it.badge === 'string' ? badges?.[it.badge] : it.badge;
                return (
                  <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) => ['hf-nav-item', isActive && 'active'].filter(Boolean).join(' ')}>
                    <Icon name={it.icon ?? 'circle-dot'} size={16} />
                    <span className="lbl">{it.label}</span>
                    {badge != null && badge !== 0 && <span className="hf-nav-badge hf-num">{badge}</span>}
                  </NavLink>
                );
              })}
            </div>
          ))}
          {footer && <div className="hf-ws-nav-foot">{footer}</div>}
        </div>
      </aside>
      )}

      {/* ── Main column ───────────────────────────────────────────────────── */}
      <div className="hf-main-col">
        <header className="hf-topbar">
          <IconButton icon="menu" label="Open navigation" size="sm" className="hf-hamburger" onClick={() => setMobileNav(true)} />
          <div className="hf-topbar-crumbs"><Breadcrumb items={crumbs} /></div>
          <div className="hf-topbar-tools">
            <Tooltip content="Search" kbd="⌘K">
              <IconButton icon="search" label="Search" size="sm" onClick={() => setPaletteOpen(true)} />
            </Tooltip>
            <VersionSkewChip />
            <NotificationTray />
            <FeedbackButton />
            {/* A phone's top bar has room for four targets, not five — and the account menu
                already carries System/Light/Dark, so the standalone toggle is the one to drop. */}
            {!isPhone && (
              <Tooltip content={theme.resolved === 'dark' ? 'Light mode' : 'Dark mode'} kbd="⌘⇧L">
                <IconButton icon={theme.resolved === 'dark' ? 'sun' : 'moon'} label={theme.resolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} size="sm" onClick={theme.toggle} />
              </Tooltip>
            )}
            {isPhone && <UserMenu placement="bottom-end" onShortcuts={() => setSheetOpen(true)} />}
          </div>
        </header>

        {tabs.tabs.length > 0 && (
          <div className="hf-ws-tabs" role="tablist" aria-label="Open records">
            {tabs.tabs.map((t) => (
              <div key={t.path} className={['hf-ws-tab', tabActive(t.path) && 'active'].filter(Boolean).join(' ')} role="presentation">
                <button type="button" role="tab" aria-selected={tabActive(t.path)} className="hf-ws-tab-btn" onClick={() => navigate(t.path)} title={t.label}>
                  {t.pinned && <Icon name="pin" size={11} className="pin" />}
                  <span className="lbl">{t.label}</span>
                </button>
                <IconButton icon={t.pinned ? 'pin-off' : 'pin'} label={t.pinned ? `Unpin ${t.label}` : `Pin ${t.label}`} size="sm" className="hf-ws-tab-pin" onClick={() => tabs.togglePin(t.path)} />
                <IconButton icon="x" label={`Close ${t.label}`} size="sm" className="hf-ws-tab-x" onClick={() => tabs.close(t.path)} />
              </div>
            ))}
            {tabs.tabs.length > 1 && (
              <button type="button" className="hf-ws-tabs-clear" onClick={() => tabs.closeAll()} title="Close unpinned tabs">Close all</button>
            )}
          </div>
        )}

        <div className="hf-body">
          <main id="hf-main" className="hf-content hf-scroll" tabIndex={-1}>
            <div className="hf-route" key={location.pathname}>
              <Outlet />
            </div>
          </main>
          <InspectorDock />
        </div>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ShortcutSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </div>
  );
}
